/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Joyride, { ACTIONS, LIFECYCLE } from 'react-joyride';
import { AvForm, AvField } from 'availity-reactstrap-validation';
import { Grid, Button, Message, Modal } from 'semantic-ui-react';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import { isEmpty } from 'lodash';

import {
  CustomSegment,
  Tooltip,
  BeaconComponent,
  NotificationMessage,
} from '../../components';

import { APP_NAME, SIDEBAR_TYPES, SUBSCRIPTIONS } from '../../utils/constant';

import {
  getAllDashboardData,
  updateNotificationReadStatus,
  deleteUserNotification,
  getSubscriptions,
  upgradeGuestUser,
  getNotificationsAll,
  getPaymentPreferences,
  setUserOnboardingOption,
  setSidebarType,
  setVisibleTips,
  getUserReminders,
} from '../../store/actions';

import ActivityStateSegment from './ActivityStateSegment';
import RecentActivitiesSegment from './RecentActivitiesSegment';
import TodoSegment from './TodoSegment';
import { get, map, find } from '../../utils/lodash';
import { isIndividualUser } from '../../utils/activity/userPermission';

import './dashboard.scss';

const userContainer = isMobile ? '.user-profile-drop' : '.user-pro-panel';

const stepsTemplate = (t) => [
  {
    target: userContainer,
    content: t('dashboard.tour.user_settings'),
    placement: 'bottom-end',
    disableBeacon: true,
  },
  {
    target: '.nav-routes-container',
    content: t('dashboard.tour.main_menu'),
    placement: 'right',
  },
  {
    target: '.dash-panel',
    content: t('dashboard.tour.dashboard'),
    placement: 'right',
  },
  {
    target: '.activity-panel',
    content: t('dashboard.tour.activity_panel'),
    placement: 'right',
  },
  {
    target: '.report-panel',
    content: t('dashboard.tour.reports'),
    placement: 'right',
  },
  {
    target: '.all-activities-panel',
    content: t('dashboard.tour.library'),
    placement: 'right',
  },
  {
    target: '.organisation-panel',
    content: t('dashboard.tour.organisation'),
    placement: 'right',
  },
  {
    target: '.activities',
    content: (
      <Trans
        i18nKey="dashboard.tour.activities"
        components={{
          amber: (
            <label className="amber-color">
              <b />
            </label>
          ),
          green: (
            <label className="green-color">
              <b />
            </label>
          ),
          blue: (
            <label className="blue-color">
              <b />
            </label>
          ),
          br: <br />,
        }}
      />
    ),
    placement: 'left',
  },
  {
    target: '.to-do',
    content: t('dashboard.tour.todo'),
    placement: 'right',
  },
  {
    target: '.notifications',
    content: t('dashboard.tour.notifications'),
    placement: 'top',
  },
  {
    target: '#helpPage',
    content: t('dashboard.tour.page_help'),
    placement: 'left',
  },
  {}, // placeholder
  {},
  {
    target: '#helpPageAction',
    content: t('dashboard.tour.show_me_around'),
    placement: 'left',
  },
];

const Dashboard = (props) => {
  const { t } = useTranslation();
  const steps = stepsTemplate(t);
  // const { visibleTips, togglerOnboardingTips } = props;
  const [infoSteps, setInfoSteps] = useState(false);
  const [visible] = useState(true);
  const [modal, setModal] = useState(false);
  const [isOpenNotificationMsg, setIsOpenNotificationMsg] = useState(false);

  const dispatch = useDispatch();
  const visibleTips = useSelector((state) => state.sidebar.isVisibleTips);
  const auth = useSelector((state) => state.authentication);
  const user = useSelector((state) => state.user);

  const latestOrgInvoice = get(auth.user, 'organisation.invoices[0]', null);

  const { organisation } = auth.user;
  const paymentPreferenceId = get(organisation, 'paymentPreferenceId', null);
  const {
    notifications,
    userReminders,
    loadingReminders,
    statUser,
    loadingStat,
    notificationsLoading,
    notificationsAll,
  } = user;

  const navigate = useNavigate();

  const btCustomer = get(auth.user, 'organisation.btCustomer', null);
  const cardEdit = btCustomer && btCustomer.btCardHolderName ? true : false;

  useEffect(() => {
    document.title = `${t('common.dashboard')} | ${APP_NAME}`;

    try {
      dispatch(getAllDashboardData());
      if (!auth.user.onboardingOption.dashboardTips) {
        setInfoSteps(true);
      }
    } catch (error) {
      // console.error('Dashboard -> componentDidMount -> error', error);
    }
  }, []);

  useEffect(() => {
    if (visibleTips) {
      // check help panel "show me around" btn clicked <- block in other updating statuses
      setInfoSteps(visibleTips);
      dispatch(setSidebarType(SIDEBAR_TYPES[0], false));
    }
  }, [visibleTips]);

  const getJoyRide = (data) => {
    const { step, action, type, lifecycle } = data;

    window.scrollTo(0, 0); // to overcome react joyride scrolling issue (joyride used wrong class position while scrolling)
    if (
      // to open left help panel
      step.target === '#helpPage' &&
      action === ACTIONS.NEXT &&
      lifecycle === LIFECYCLE.COMPLETE
    ) {
      setInfoSteps(false);
      // trigger only before reach to next location
      dispatch(setSidebarType(SIDEBAR_TYPES[0], true));
      setTimeout(() => {
        setInfoSteps(true);
      }, 400);
    }

    if (type === 'tour:end') {
      setInfoSteps(false);

      if (!auth.user.onboardingOption.dashboardTips) {
        // only trigger for new user/ users not toured whole trip
        auth.user.onboardingOption.dashboardTips = true;
        dispatch(setUserOnboardingOption(auth.user)); // save in db
      }
      dispatch(setSidebarType(SIDEBAR_TYPES[0], false)); // hide help panel and off the onboarding flow in sidebar
      dispatch(setVisibleTips(false)); // hide help panel and off the onboarding flow in sidebar
    }
  };

  const toggle = () => {
    setModal(!modal);
  };

  const changeToBillingPage = () => {
    navigate('/account/billing');
  };

  const daysRemaining = (trialEndDate) => {
    let eventDate = moment(trialEndDate);
    let todaysDate = moment();
    if (moment().isSame(eventDate, 'day')) return -1;
    return eventDate.diff(todaysDate, 'days');
  };

  const msg = find(notificationsAll.docs, { isRead: false });
  if (msg && !msg.isRead && !isOpenNotificationMsg)
    setIsOpenNotificationMsg(true);

  let passedDate = false;
  if (!isEmpty(latestOrgInvoice)) {
    if (
      latestOrgInvoice.nextBillingDate &&
      moment().diff(latestOrgInvoice.nextBillingDate, 'days') !== 0
    ) {
      passedDate = true;
    }
  }

  let guestIndividual = false;
  if (!isEmpty(organisation)) {
    if (
      organisation.trialExpiredAt === null &&
      !passedDate &&
      organisation.type === 'INDIVIDUAL'
    ) {
      guestIndividual = true;
    }
  }

  const duration = isIndividualUser(auth.user)
    ? daysRemaining(organisation.trialExpiredAt)
    : 0;

  return (
    <Fragment>
      <div className="add-goal">
        <Modal size="tiny" open={modal}>
          <Modal.Header>{t('dashboard.upgrade.modal_title')}</Modal.Header>
          <Modal.Content>
            <UpgradeSubscription toggle={() => toggle()} {...props} />
          </Modal.Content>
        </Modal>
      </div>

      <Grid.Row>
        <Grid.Column width={16} className="p-0 mb-3">
          {visible && guestIndividual ? (
            <Message className="upgrade-notification">
              <div className="txt-button-align">
                <div className="upg-text-outline">
                  <div className="upgrade-text">
                    {t('dashboard.upgrade.header')}
                  </div>
                  <div className="upgrade-text-sub">
                    {t(
                      'dashboard.upgrade.click_the_upgrade_button_to_start_your_subscription'
                    )}
                  </div>
                </div>

                <div className=" btn-outline">
                  <Button
                    type="submit"
                    className="btn btn-primary verify-button upgrade-button"
                    content={t('common.upgrade')}
                    onClick={() => changeToBillingPage()}
                  />
                </div>
              </div>
            </Message>
          ) : null}

          {isIndividualUser(auth.user) && duration >= 0 ? (
            <Message className="upgrade-notification">
              <div className="txt-button-align">
                <div className="upg-text-outline">
                  <div className="upgrade-text">
                    {t('dashboard.upgrade.header')}
                  </div>
                  <div className="upgrade-text-sub">
                    {paymentPreferenceId === 1
                      ? duration === 0
                        ? t('dashboard.upgrade.ends_today')
                        : t('dashboard.upgrade.trial_remaining', {
                            count: duration,
                          })
                      : duration === 0
                        ? t('dashboard.upgrade.ends_today')
                        : t('dashboard.upgrade.trial_remaining', {
                            count: duration,
                          })}
                    {paymentPreferenceId === 1 && !cardEdit
                      ? ' ' + t('dashboard.upgrade.add_card')
                      : ''}
                  </div>
                </div>
                {paymentPreferenceId === 1 && !cardEdit ? (
                  <div className=" btn-outline">
                    <Button
                      type="submit"
                      className="btn btn-primary verify-button upgrade-button"
                      content={t('dashboard.upgrade.add_card_button')}
                      onClick={() => navigate('/account/billing')}
                    />
                  </div>
                ) : null}
              </div>
            </Message>
          ) : null}
        </Grid.Column>
      </Grid.Row>

      <Grid.Row className={isMobile ? null : 'dashboard-fill-row'}>
        <Grid.Column
          width={isMobile ? (!infoSteps && isMobile ? 16 : 15) : 10}
          className="dashboard-column"
        >
          {msg && !msg.isRead && isOpenNotificationMsg ? (
            <NotificationMessage
              classNames="notification-segment"
              message={get(msg, 'notification.message', '')}
              title={get(msg, 'notification.title', '')}
              style={get(msg, 'notification.style', 'warning')}
              updateNotificationReadStatus={() => {
                dispatch(updateNotificationReadStatus(msg.id, true));
                setIsOpenNotificationMsg(false);
              }}
            />
          ) : null}

          <CustomSegment
            title={t('common.todo').toUpperCase()}
            /*
             * Note:  to-do class name used for help tip showing class name (don't change it or remove it)
             */
            className="to-do"
            headerStyle={{ marginLeft: 12 }}
          >
            <TodoSegment
              todoList={userReminders}
              loading={loadingReminders}
              getUserReminders={() => dispatch(getUserReminders())}
              style={{ paddingRight: '1.714rem' }}
            />
          </CustomSegment>
          {isMobile ? (
            <div>
              <CustomSegment
                title={t('common.activities').toUpperCase()}
                /*
                 * Note: activities class name used for help tip showing class name (don't change it or remove it)
                 */
                className="activities"
                extraMargin
                headerStyle={{ marginLeft: 35 }}
              >
                <ActivityStateSegment
                  user={auth.user}
                  statUser={statUser}
                  loading={loadingStat}
                />
              </CustomSegment>
              <CustomSegment
                title={t('notifications.title').toUpperCase()}
                icon="bell"
                /*
                 * Note: notifications class name used for help tip showing class name (don't change it or remove it)
                 */
                className="notifications"
              >
                <RecentActivitiesSegment
                  userNotifications={notifications}
                  userNotificationsAll={notificationsAll}
                  loading={notificationsLoading.notification}
                  updateNotificationReadStatus={(id) =>
                    dispatch(updateNotificationReadStatus(id, true))
                  }
                  deleteUserNotification={(id) =>
                    dispatch(deleteUserNotification(id))
                  }
                  {...props}
                />
                {/* } */}
              </CustomSegment>
            </div>
          ) : null}
        </Grid.Column>
        {isMobile ? null : (
          <Grid.Column width={6} className="pr-0">
            <CustomSegment
              title={t('common.activities').toUpperCase()}
              className="activities"
            >
              <ActivityStateSegment
                user={auth.user}
                statUser={statUser}
                loading={loadingStat}
              />
            </CustomSegment>

            <CustomSegment
              title={t('notifications.title').toUpperCase()}
              className="notifications"
              icon="bell"
              style={{ marginTop: '1.714rem' }}
            >
              <RecentActivitiesSegment
                userNotifications={notifications}
                userNotificationsAll={notificationsAll}
                loading={notificationsLoading.notification}
                updateNotificationReadStatus={(id) =>
                  dispatch(updateNotificationReadStatus(id, true))
                }
                getNotificationsAll={(page, limit) => {
                  dispatch(getNotificationsAll(page, limit));
                }}
                deleteUserNotification={(id) =>
                  dispatch(deleteUserNotification(id))
                }
                {...props}
              />
            </CustomSegment>
          </Grid.Column>
        )}
        <Joyride
          autoStart
          callback={(data) => getJoyRide(data)}
          run={infoSteps}
          steps={steps}
          continuous
          disableOverlayClose={true} // disable overlay close <- need to go whole trip
          disableCloseOnEsc={true} // disable overlay close [ESC btn]<- need to go whole trip
          tooltipComponent={Tooltip}
          beaconComponent={BeaconComponent}
          scrollToFirstStep
          disableScrolling={!isMobile} // disable scroll in web view <- to resolve wrong target at help panel in web view
        />
      </Grid.Row>
    </Fragment>
  );
};

const UpgradeSubscription = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [state, setState] = useState({
    subscription: '',
    paymentMethod: '1',
  });

  const subscriptions = useSelector((state) => state.extra.subscriptions);
  const paymentPreferences = useSelector(
    (state) => state.extra.paymentPreferences
  );

  useEffect(() => {
    dispatch(getSubscriptions());
    dispatch(getPaymentPreferences());
  }, []); // Empty dependency array ensures this effect runs once on mount

  const handleValidSubmit = async (event, values) => {
    try {
      const { toggle } = props;
      dispatch(upgradeGuestUser(values));
      toggle();
    } catch (error) {}
  };

  const onChangeSubs = (subscription) => {
    setState((prevState) => ({
      ...prevState,
      subscription,
    }));
  };

  const onChangeMethod = (paymentMethod) => {
    setState((prevState) => ({
      ...prevState,
      paymentMethod,
    }));
  };

  const renderGroups = () => {
    return map(subscriptions, (item, i) => {
      const isIndividual = item.accountTypes.includes('INDIVIDUAL');
      if (!isIndividual) return null;
      return (
        <option key={i} value={item.id}>
          {item.title}
        </option>
      );
    });
  };

  const renderPaymentPreference = () => {
    return map(paymentPreferences, (item, i) => {
      // Manual Transaction-id 2 removed from guest user upgrade modal
      if (item.id === 2) return null;
      return (
        <option key={i} value={item.id}>
          {item.title}
        </option>
      );
    });
  };

  const { subscription } = state;
  const { toggle } = props;

  const user = useSelector((state) => state.user);
  const subDetails = find(
    SUBSCRIPTIONS(t),
    (sub) => sub.id === Number(subscription)
  );

  return (
    <div className="container-upgradeSubscription">
      <AvForm
        onValidSubmit={(event, values) => handleValidSubmit(event, values)}
      >
        <div className="your-plan-subscription">
          {t('dashboard.upgrade.plan_title')}
        </div>

        <AvField
          type="select"
          name="subscription"
          label={t('dashboard.upgrade.subscription_label')}
          className="sub-inputs"
          validate={{
            required: {
              value: true,
              errorMessage: t('dashboard.upgrade.subscription_required'),
            },
          }}
          onChange={(e) => onChangeSubs(e.target.value)}
        >
          <option value="" defaultValue disabled>
            {t('dashboard.upgrade.subscription_placeholder')}
          </option>
          {renderGroups()}
        </AvField>
        {subDetails ? (
          <div className="sub-detail-container-verification">
            <label className="plan-include-text">
              {t('dashboard.upgrade.includes')}
            </label>

            <Grid columns={1} stackable>
              <Grid.Row className="detail">
                {map(subDetails.includes, (inc, i) => (
                  <div key={i}>
                    <label className={'orangeCorrectIcon'}>
                      &#10004;&nbsp;&nbsp;
                    </label>
                    <label className={'planList'}>{inc}</label>
                  </div>
                ))}
              </Grid.Row>
            </Grid>
          </div>
        ) : null}

        <div className="payment-text">
          {t('dashboard.upgrade.payment_method')}
        </div>

        <AvField
          type="select"
          name="paymentMethod"
          className="sub-inputs"
          validate={{
            required: {
              value: true,
              errorMessage: t('dashboard.upgrade.payment_required'),
            },
          }}
          onChange={(e) => onChangeMethod(e.target.value)}
          value={state.paymentMethod}
        >
          <option value="" disabled defaultValue>
            {t('dashboard.upgrade.payment_method_placeholder')}
          </option>
          {renderPaymentPreference()}
        </AvField>

        <div className="model-buttons">
          <Button
            className="goal-cancel-button"
            type="button"
            onClick={() => toggle()}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            loading={user.guestUserLoading}
            disabled={user.guestUserLoading}
            content={t('actions.save')}
            type="submit"
            className="goal-create-button"
          />
        </div>
      </AvForm>
    </div>
  );
};

export default Dashboard;
